import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useToast } from '@/components/ui/toast';
import type { Creator, CreatorStatus } from '@/types';

export function useUpdateCreator() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (vars: { id: string; data: Partial<Creator> }) =>
      pb.collection('leads').update<Creator>(vars.id, vars.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creators'] });
      qc.invalidateQueries({ queryKey: ['creator'] });
      toast('Creator actualizado', 'success');
    },
    onError: () => toast('Error al actualizar creator', 'error'),
  });
}

export function useUpdateCreatorStatus() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (vars: { id: string; status: CreatorStatus }) =>
      pb.collection('leads').update(vars.id, { status: vars.status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creators'] });
      qc.invalidateQueries({ queryKey: ['creator'] });
      toast('Status actualizado', 'success');
    },
    onError: () => toast('Error al cambiar status', 'error'),
  });
}

type SanctionRes = {
  success: boolean;
  sanctions_count: number;
  eur_deducted: number;
  banned: boolean;
};

type SanctionVars = { id: string; reason: string; notes?: string };

const SANCTION_QKS = [
  ['creators'],
  ['creator'],
  ['leaderboard'],
  ['team-members'],
  ['team-members-admin'],
  ['personal-stats'],
  ['payments-summary'],
];

function sanctionMessage(r: SanctionRes): string {
  if (r.banned) {
    return `Sanción aplicada (${r.sanctions_count}/5). Collector baneado.`;
  }
  return `Sanción aplicada (${r.sanctions_count}/5) — $${r.eur_deducted} descontados`;
}

export function useSanctionCreator() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (vars: SanctionVars) =>
      pb.send<SanctionRes>(`/api/leads/sanction/${vars.id}`, {
        method: 'POST',
        body: { reason: vars.reason, notes: vars.notes ?? '' },
      }),
    onSuccess: (r) => {
      SANCTION_QKS.forEach((qk) => qc.invalidateQueries({ queryKey: qk }));
      toast(sanctionMessage(r), r.banned ? 'error' : 'success');
    },
    onError: (err: Error) =>
      toast(err.message || 'Error al aplicar sanción', 'error'),
  });
}

export {
  useHardDeleteCreator,
  useRestoreCreator,
  useSoftDeleteCreator,
} from './use-creator-lifecycle';

export { useAppealLead, useUserDeleteLead } from './use-creator-fase-b';
